/**
 * A montagem do diagrama — o desenho em blocos, para arrastar.
 *
 * **É o `Editar` do Sistema de hoje**: duas colunas de componentes com o
 * principal no meio, e cada bloco é o desenho daquele componente. No legado as
 * colunas são `connectedSortable` do jQuery UI; aqui é arrastar nativo, sem
 * biblioteca.
 *
 * **Uma carga só.** Pedir componente por componente custaria 2,5 s *cada* — o
 * diagrama 7141, com 129, levaria cinco minutos. A rota de montagem carrega o
 * diagrama uma vez e devolve os 129 desenhos em 8 s.
 *
 * **Reordenar não pede nada ao servidor.** Mudar a ordem dentro da coluna não
 * muda o traço de ninguém: muda a pilha, e a tela já tem os desenhos. Só a troca
 * de lado muda o traço — o motor espelha o componente com `passos.Right()` —, e
 * aí só o que atravessou é rebuscado.
 *
 * **O `<style>` vem uma vez**, fora dos blocos: é o mesmo para todos, e repetido
 * dentro de cada SVG custava 250 kB dos 492 do 7141.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"

export type BlocoDaMontagem = {
  diagramaComponenteId: number
  componenteId: number
  codigo: string
  descricao: string
  posicao: string
  ordem: number
  /** `false` = o motor esconde este componente; ele aparece dentro da ligação de outro. */
  exibir: boolean
  terminais: number
  /** Vazio quando `exibir` é falso, ou quando o motor devolve traço nenhum (jump). */
  svg: string
  larguraPx: number
  alturaPx: number
}

export type MontagemDoDiagrama = {
  diagramaId: number
  /** Onde o componente principal fica. `center` = há coluna dos dois lados. */
  posicao: string
  /** O `<style>` do diagrama, uma vez só — vai no contêiner, não em cada bloco. */
  estilos: string
  principalSvg: string
  principalLarguraPx: number
  blocos: BlocoDaMontagem[]
  milissegundos: number
  /** Quanto cada coluna entra por cima do principal, em px do desenho. */
  sobreposicaoPx: number
}

export function usarMontagem(diagramaId: number) {
  return useQuery({
    queryKey: ["diagrama-montagem", diagramaId] as const,
    enabled: diagramaId > 0,
    staleTime: 0,
    queryFn: () => obter<MontagemDoDiagrama>(`api/diagramas/${diagramaId}/montagem`, {}),
  })
}

/**
 * Grava a ordem das duas colunas — o `json_ordem` do legado, que é o mesmo
 * `PUT .../ordem` da lista de componentes.
 *
 * **Ele grava `posicao` junto com `ordem`**: mandar o id na lista da outra coluna
 * é o que move o componente de lado. Por isso a tela manda sempre as duas.
 *
 * Não invalida a montagem inteira de propósito — 8 s no maior diagrama. Quem
 * atravessou é rebuscado sozinho pela tela; quem só subiu ou desceu não precisa
 * de traço novo.
 */
export function usarGravarMontagem(projetoId: number, diagramaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (colunas: { left: number[]; right: number[] }) => {
      const q = new URLSearchParams()
      q.set("left", colunas.left.join(","))
      q.set("right", colunas.right.join(","))
      await api.put(`api/diagramas/${diagramaId}/ordem?${q}`, undefined, noProjeto(projetoId))
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["diagrama-componentes", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagrama-desenho-estado", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagramas", projetoId] })
    },
  })
}

/** O desenho de um componente só, para quem trocou de lado. */
export async function desenhoDoBloco(diagramaComponenteId: number) {
  return obter<{ svg: string; svgLarguraPx: number; svgAlturaPx: number }>(
    `api/diagramas/componentes/${diagramaComponenteId}/desenho`, { grid: false })
}

/**
 * Desmembra o componente inteiro — o "Desagrupar Tudo" do modal antigo.
 *
 * A primeira ligação fica onde está; cada uma das outras ganha um componente
 * novo. É o caminho mais usado do desagrupar: desfaz sem pedir para montar
 * grupo nenhum.
 */
export function usarDesagruparTudo(projetoId: number, diagramaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (diagramaComponenteId: number) => {
      const r = await api.post<{ componentesCriados: number }>(
        `api/diagramas/componentes/${diagramaComponenteId}/desagrupar-tudo`,
        undefined, noProjeto(projetoId))
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["diagrama-montagem", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagrama-componentes", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagrama-desenho-estado", diagramaId] })
    },
  })
}

/** Um terminal do componente, para montar os grupos do desagrupar por partes. */
export type LigacaoParaDesagrupar = {
  id: number
  pinoutId: number
  pino: string
  circuitoDescricao: string
  posicao: string
  pinoutInternoId: number
}

/**
 * Desagrupar por grupos — o "Finalizar" do modal antigo.
 *
 * O corpo é a string que a UI antiga monta: grupos separados por `-`, itens por
 * `,`, cada item `id[pinout=interno/posicao]`. O primeiro grupo fica no
 * componente atual; cada grupo seguinte ganha um componente novo.
 */
export function usarDesagruparPorGrupos(projetoId: number, diagramaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, grupos }: { id: number; grupos: LigacaoParaDesagrupar[][] }) => {
      const txt = grupos
        .filter((g) => g.length > 0)
        .map((g) => g.map((l) =>
          `${l.id}[${l.pinoutId}=${l.pinoutInternoId}/${l.posicao}]`).join(","))
        .join("-")
      const q = new URLSearchParams({ diagramaComponenteId: String(id), txt })
      await api.post(`api/diagramas/desagrupar?${q}`, undefined, noProjeto(projetoId))
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["diagrama-montagem", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagrama-componentes", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagrama-desenho-estado", diagramaId] })
    },
  })
}
